import React, { useState, useCallback } from 'react';
import { AdminPageHeader } from './components/AdminPageHeader';
import { AdminStatCard } from './components/AdminStatCard';

// ============================================================================
// Types
// ============================================================================

export interface ErrorLog {
  id: number;
  errorClass: string;
  message: string;
  source: string | null;
  emailSent: boolean;
  createdAt: string;
}

export interface ErrorByClass {
  errorClass: string;
  count: number;
}

export interface AdminErrorLogsPageProps {
  errorLogs: ErrorLog[];
  statistics: {
    total: number;
    last24h: number;
    lastWeek: number;
    unsent: number;
  };
  errorsByClass: ErrorByClass[];
  filteredCount: number;
  isFiltering: boolean;

  // Current filters
  currentErrorClass: string;
  currentMessage: string;

  // URLs
  dashboardPath: string;
  errorLogsPath: string;
  errorLogPath: (id: number) => string;
  deleteErrorLogPath: (id: number) => string;
  bulkDeletePath: string;

  // CSRF token
  csrfToken: string;

  // Labels
  labels: {
    title: string;
    subtitle: string;
    backLabel: string;
    // Stats
    totalErrorsLabel: string;
    allTime: string;
    last24hLabel: string;
    recentErrors: string;
    last7DaysLabel: string;
    weeklyErrors: string;
    pendingEmailLabel: string;
    notYetEmailed: string;
    // Error types
    topErrorTypesTitle: string;
    errorClassColumn: string;
    countColumn: string;
    // Search
    errorClassLabel: string;
    searchByClassPlaceholder: string;
    messageLabel: string;
    searchByMessagePlaceholder: string;
    searchButton: string;
    clearButton: string;
    // Bulk delete
    showingFiltered: string;
    totalLabel: string;
    errors: string;
    deleteFiltered: string;
    deleteAll: string;
    // Table
    recentErrorLogsTitle: string;
    filteredErrorLogsTitle: string;
    timeColumn: string;
    sourceColumn: string;
    emailedColumn: string;
    actionsColumn: string;
    yesLabel: string;
    noLabel: string;
    notApplicable: string;
    agoLabel: string;
    noErrorsYet: string;
    everythingRunning: string;
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminErrorLogsPage({
  errorLogs,
  statistics,
  errorsByClass,
  filteredCount,
  isFiltering,
  currentErrorClass,
  currentMessage,
  dashboardPath,
  errorLogsPath,
  errorLogPath,
  deleteErrorLogPath,
  bulkDeletePath,
  csrfToken,
  labels,
}: AdminErrorLogsPageProps) {
  const [errorClassFilter, setErrorClassFilter] = useState(currentErrorClass);
  const [messageFilter, setMessageFilter] = useState(currentMessage);

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const url = new URL(errorLogsPath, window.location.origin);
      if (errorClassFilter) url.searchParams.set('error_class', errorClassFilter);
      if (messageFilter) url.searchParams.set('message', messageFilter);
      window.location.href = url.toString();
    },
    [errorLogsPath, errorClassFilter, messageFilter]
  );

  const handleDelete = useCallback(
    (id: number) => {
      if (!confirm('Delete this error log?')) return;

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = deleteErrorLogPath(id);

      const methodInput = document.createElement('input');
      methodInput.type = 'hidden';
      methodInput.name = '_method';
      methodInput.value = 'DELETE';
      form.appendChild(methodInput);

      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);

      document.body.appendChild(form);
      form.submit();
    },
    [deleteErrorLogPath, csrfToken]
  );

  const handleBulkDelete = useCallback(() => {
    const confirmMessage = isFiltering
      ? `Delete ${filteredCount} filtered error log(s)? This action cannot be undone.`
      : `Delete ALL ${filteredCount} error log(s)? This action cannot be undone.`;

    if (!confirm(confirmMessage)) return;

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = bulkDeletePath;

    const methodInput = document.createElement('input');
    methodInput.type = 'hidden';
    methodInput.name = '_method';
    methodInput.value = 'DELETE';
    form.appendChild(methodInput);

    const csrfInput = document.createElement('input');
    csrfInput.type = 'hidden';
    csrfInput.name = 'authenticity_token';
    csrfInput.value = csrfToken;
    form.appendChild(csrfInput);

    if (currentErrorClass) {
      const errorClassInput = document.createElement('input');
      errorClassInput.type = 'hidden';
      errorClassInput.name = 'error_class';
      errorClassInput.value = currentErrorClass;
      form.appendChild(errorClassInput);
    }

    if (currentMessage) {
      const messageInput = document.createElement('input');
      messageInput.type = 'hidden';
      messageInput.name = 'message';
      messageInput.value = currentMessage;
      form.appendChild(messageInput);
    }

    document.body.appendChild(form);
    form.submit();
  }, [bulkDeletePath, csrfToken, isFiltering, filteredCount, currentErrorClass, currentMessage]);

  return (
    <div>
      <AdminPageHeader
        title={labels.title}
        subtitle={labels.subtitle}
        icon="exclamation-triangle"
        backLink={{ href: dashboardPath, label: labels.backLabel }}
      />

      {/* Statistics Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-6 col-xl-3">
          <AdminStatCard
            title={labels.totalErrorsLabel}
            value={statistics.total}
            subtitle={labels.allTime}
            icon="bug"
            iconColor="danger"
            borderColor="danger"
            valueColor="danger"
          />
        </div>
        <div className="col-md-6 col-xl-3">
          <AdminStatCard
            title={labels.last24hLabel}
            value={statistics.last24h}
            subtitle={labels.recentErrors}
            icon="clock-history"
            iconColor="warning"
            borderColor="warning"
            valueColor="warning"
          />
        </div>
        <div className="col-md-6 col-xl-3">
          <AdminStatCard
            title={labels.last7DaysLabel}
            value={statistics.lastWeek}
            subtitle={labels.weeklyErrors}
            icon="calendar-week"
            iconColor="primary"
            borderColor="primary"
            valueColor="primary"
          />
        </div>
        <div className="col-md-6 col-xl-3">
          <AdminStatCard
            title={labels.pendingEmailLabel}
            value={statistics.unsent}
            subtitle={labels.notYetEmailed}
            icon="envelope"
            iconColor="info"
            borderColor="info"
            valueColor="info"
          />
        </div>
      </div>

      {/* Errors by Type */}
      {errorsByClass.length > 0 && (
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <h5 className="card-title mb-3">
              <i className="bi bi-bar-chart me-2" />
              {labels.topErrorTypesTitle}
            </h5>
            <div className="table-responsive">
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>{labels.errorClassColumn}</th>
                    <th className="text-end">{labels.countColumn}</th>
                  </tr>
                </thead>
                <tbody>
                  {errorsByClass.map((item) => (
                    <tr key={item.errorClass}>
                      <td>
                        <a
                          href={`${errorLogsPath}?error_class=${encodeURIComponent(item.errorClass)}`}
                          className="text-decoration-none"
                        >
                          <code>{item.errorClass}</code>
                        </a>
                      </td>
                      <td className="text-end">{item.count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <form onSubmit={handleSearch} className="d-flex flex-wrap gap-3 align-items-end">
            <div className="flex-grow-1" style={{ minWidth: 200 }}>
              <label className="form-label small text-muted mb-1">
                {labels.errorClassLabel}
              </label>
              <input
                type="text"
                className="form-control"
                placeholder={labels.searchByClassPlaceholder}
                value={errorClassFilter}
                onChange={(e) => setErrorClassFilter(e.target.value)}
              />
            </div>
            <div className="flex-grow-1" style={{ minWidth: 200 }}>
              <label className="form-label small text-muted mb-1">
                {labels.messageLabel}
              </label>
              <input
                type="text"
                className="form-control"
                placeholder={labels.searchByMessagePlaceholder}
                value={messageFilter}
                onChange={(e) => setMessageFilter(e.target.value)}
              />
            </div>
            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-primary">
                {labels.searchButton}
              </button>
              {isFiltering && (
                <a href={errorLogsPath} className="btn btn-outline-secondary">
                  {labels.clearButton}
                </a>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Bulk Delete Section */}
      {filteredCount > 0 && (
        <div className="card shadow-sm mb-4 border-danger">
          <div className="card-body d-flex justify-content-between align-items-center">
            <div>
              {isFiltering ? (
                <span className="text-muted">
                  <i className="bi bi-funnel me-1" />
                  {labels.showingFiltered}{' '}
                  <strong className="text-danger">{filteredCount.toLocaleString()}</strong>{' '}
                  {labels.errors}
                </span>
              ) : (
                <span className="text-muted">
                  <i className="bi bi-database me-1" />
                  {labels.totalLabel}:{' '}
                  <strong className="text-danger">{filteredCount.toLocaleString()}</strong>{' '}
                  {labels.errors}
                </span>
              )}
            </div>
            <button type="button" className="btn btn-danger" onClick={handleBulkDelete}>
              <i className="bi bi-trash me-1" />
              {isFiltering
                ? `${labels.deleteFiltered} ${filteredCount.toLocaleString()}`
                : `${labels.deleteAll} ${filteredCount.toLocaleString()}`}
            </button>
          </div>
        </div>
      )}

      {/* Error Logs List */}
      <div className="card shadow-sm">
        <div className="card-header bg-white">
          <h5 className="mb-0">
            <i className="bi bi-list-ul me-2" />
            {isFiltering ? labels.filteredErrorLogsTitle : labels.recentErrorLogsTitle}
          </h5>
        </div>
        <div className="card-body p-0">
          {errorLogs.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{labels.timeColumn}</th>
                    <th>{labels.errorClassColumn}</th>
                    <th>{labels.messageLabel}</th>
                    <th>{labels.sourceColumn}</th>
                    <th>{labels.emailedColumn}</th>
                    <th>{labels.actionsColumn}</th>
                  </tr>
                </thead>
                <tbody>
                  {errorLogs.map((log) => (
                    <tr key={log.id}>
                      <td>
                        <span title={log.createdAt}>
                          {formatTimeAgo(log.createdAt)} {labels.agoLabel}
                        </span>
                      </td>
                      <td>
                        <code className="text-danger">{log.errorClass}</code>
                      </td>
                      <td>
                        <div
                          className="text-truncate"
                          style={{ maxWidth: 300 }}
                          title={log.message}
                        >
                          {log.message.length > 100
                            ? `${log.message.substring(0, 100)}...`
                            : log.message}
                        </div>
                      </td>
                      <td>
                        {log.source ? (
                          <small className="text-muted">
                            {log.source.length > 30
                              ? `${log.source.substring(0, 30)}...`
                              : log.source}
                          </small>
                        ) : (
                          <span className="text-muted fst-italic">{labels.notApplicable}</span>
                        )}
                      </td>
                      <td>
                        {log.emailSent ? (
                          <span className="badge bg-success">{labels.yesLabel}</span>
                        ) : (
                          <span className="badge bg-secondary">{labels.noLabel}</span>
                        )}
                      </td>
                      <td>
                        <a
                          href={errorLogPath(log.id)}
                          className="btn btn-sm btn-outline-primary me-1"
                        >
                          <i className="bi bi-eye" />
                        </a>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(log.id)}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-check-circle text-success" style={{ fontSize: '3rem' }} />
              <p className="mt-3 mb-0">
                {labels.noErrorsYet} {labels.everythingRunning}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminErrorLogsPage;
